import { Link, useParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import useSeo from "@/hooks/useSeo";
import { CATEGORIES, CASES } from "@/mocks/archive";

export default function CaseDetail() {
  const { slug = "" } = useParams();
  const item = CASES.find((c) => c.slug === slug);
  const cat = item ? CATEGORIES.find((c) => c.slug === item.category)! : null;

  const title = item
    ? `${item.title} - ${cat?.name || "黑料"}档案`
    : "档案不存在 - 明星黑料分类档案盘点";
  const desc = item
    ? `${item.excerpt} 收录于「${cat?.name || "黑料"}」分类，标签：${item.tags.join("、")}。`
    : "未找到对应的黑料档案条目，请返回分类档案库重新检索。";

  const jsonLd = item
    ? [
        {
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: item.title,
          datePublished: item.published,
          inLanguage: "zh-CN",
          articleSection: `${cat?.name || ""}黑料`,
          keywords: item.tags.join(", "),
          articleBody: item.body.map((s) => `${s.h}。${s.p.join("")}`).join(""),
          author: { "@type": "Organization", name: "黑料档案编辑部" },
          publisher: { "@type": "Organization", name: "黑料档案 · 八卦志" },
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://gjkmv.cn/" },
            { "@type": "ListItem", position: 2, name: "分类档案库", item: "https://gjkmv.cn/archive" },
            { "@type": "ListItem", position: 3, name: `${cat?.name || ""}黑料`, item: `https://gjkmv.cn/archive?cat=${cat?.slug || ""}` },
            { "@type": "ListItem", position: 4, name: item.title },
          ],
        },
      ]
    : [
        {
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: title,
          inLanguage: "zh-CN",
        },
      ];

  useSeo({ title, description: desc, path: `/case/${slug}`, jsonLd });

  if (!item || !cat) {
    return (
      <PageShell>
        <section className="bg-background-50 pt-28 md:pt-32 pb-24 text-center px-4">
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">该档案暂未收录</h1>
          <p className="mt-3 text-foreground-600">这条档案可能已被整理或不存在。</p>
          <Link to="/archive" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-600 text-background-50 px-5 py-2.5 text-sm font-semibold hover:bg-primary-700 transition-colors whitespace-nowrap">
            <i className="ri-arrow-left-line" /> 返回分类档案库
          </Link>
        </section>
      </PageShell>
    );
  }

  const related = CASES.filter((c) => c.category === item.category && c.slug !== item.slug).slice(0, 3);
  const sameCatList = CASES.filter((c) => c.category === item.category);
  const idx = sameCatList.findIndex((c) => c.slug === item.slug);
  const prevC = sameCatList[idx - 1];
  const nextC = sameCatList[idx + 1];

  return (
    <PageShell>
      <div className="bg-background-100 pt-28 md:pt-32">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/archive" className="hover:text-primary-600 transition-colors whitespace-nowrap">分类档案库</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={`/archive?cat=${cat.slug}`} className="hover:text-primary-600 transition-colors whitespace-nowrap">{cat.name}黑料</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800 whitespace-nowrap truncate max-w-[200px]">{item.title}</span>
          </nav>
        </div>
      </div>

      <div className="bg-background-50">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6 py-8 md:py-12 grid gap-10 lg:grid-cols-[200px_1fr_220px] lg:gap-8">
          {/* 左侧目录 */}
          <aside className="hidden lg:block">
            <div className="sticky top-28">
              <p className="text-xs font-semibold tracking-[0.18em] text-foreground-400 uppercase">目录索引</p>
              <nav className="mt-4 flex flex-col gap-2 border-l border-background-300 pl-4" aria-label="本文目录">
                <a href="#top" className="text-xs text-foreground-500 hover:text-primary-700 transition-colors">档案概览</a>
                {item.body.map((s, i) => (
                  <a key={s.h} href={`#sec-${i}`} className="text-xs text-foreground-500 hover:text-primary-700 transition-colors leading-5">
                    {s.h}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* 中央文章 */}
          <article className="min-w-0" id="top">
            <div className="flex flex-wrap items-center gap-2">
              <Link to={`/archive?cat=${cat.slug}`} className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium ${cat.accent === "accent" ? "bg-accent-100 text-accent-800 hover:bg-accent-200" : "bg-primary-100 text-primary-800 hover:bg-primary-200"} transition-colors`}>
                <i className={cat.icon} /> {cat.name}黑料档案
              </Link>
              <span className="inline-flex items-center gap-1 text-xs text-foreground-400"><i className="ri-time-line" /> 更新于 {item.published}</span>
              <span className="inline-flex items-center gap-1 text-xs text-foreground-400"><i className="ri-book-read-line" /> 阅读约 {item.read}</span>
            </div>

            <h1 className="mt-4 font-heading text-2xl md:text-[2rem] leading-snug font-black text-foreground-950">
              {item.title}
            </h1>
            <p className="mt-3 text-base md:text-lg leading-8 font-medium text-primary-800">{item.kicker}</p>
            <p className="mt-4 text-sm md:text-base leading-7 text-foreground-600">{item.excerpt}</p>

            <div className="mt-6 flex flex-wrap gap-2 border-y border-background-300/70 py-3">
              {item.tags.map((t) => (
                <Link key={t} to={`/archive?cat=${cat.slug}&tag=${encodeURIComponent(t)}`} className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900 hover:bg-secondary-200 transition-colors whitespace-nowrap">
                  #{t}
                </Link>
              ))}
              <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-accent-700"><i className="ri-fire-fill" /> 事件热度 {item.heat}/5</span>
            </div>

            {/* 正文 */}
            <div className="mt-8 space-y-8">
              {item.body.map((s, i) => (
                <section key={s.h} id={`sec-${i}`} className="scroll-mt-28">
                  <h2 className="flex items-center gap-3 font-heading text-lg md:text-xl font-bold text-foreground-950">
                    <span className="grid place-items-center w-7 h-7 rounded-lg bg-primary-600 text-background-50 text-xs font-heading shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    {s.h}
                  </h2>
                  {s.p.map((p, pi) => (
                    <p key={pi} className="mt-4 text-[15px] leading-8 text-foreground-700">{p}</p>
                  ))}
                </section>
              ))}
            </div>

            {/* 提示条 */}
            <div className="mt-10 flex items-start gap-3 rounded-xl border border-background-300/70 bg-background-100 p-4">
              <i className="ri-information-line text-lg text-primary-600 shrink-0" />
              <p className="text-xs md:text-sm leading-6 text-foreground-600">
                说明：本条档案为娱乐信息与舆情观察整理，不代表司法定性。最终事实请以权威司法机构、品牌方及当事人官方披露为准，请勿据此对任何个人作出未经证实的判断。
              </p>
            </div>

            {/* 上下篇 */}
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {prevC ? (
                <Link to={`/case/${prevC.slug}`} className="group rounded-xl border border-background-300/70 bg-background-100 p-4 hover:border-primary-300 transition-colors">
                  <span className="text-xs text-foreground-400">← 上一篇</span>
                  <p className="mt-1.5 text-sm font-medium text-foreground-900 group-hover:text-primary-700 line-clamp-2 transition-colors">{prevC.title}</p>
                </Link>
              ) : <div />}
              {nextC ? (
                <Link to={`/case/${nextC.slug}`} className="group rounded-xl border border-background-300/70 bg-background-100 p-4 text-right hover:border-primary-300 transition-colors">
                  <span className="text-xs text-foreground-400">下一篇 →</span>
                  <p className="mt-1.5 text-sm font-medium text-foreground-900 group-hover:text-primary-700 line-clamp-2 transition-colors">{nextC.title}</p>
                </Link>
              ) : <div />}
            </div>
          </article>

          {/* 右侧信息 */}
          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-5">
              <div className="rounded-xl border border-background-300/70 bg-background-100 p-5">
                <p className="flex items-center gap-2 text-xs font-semibold tracking-wide text-foreground-700"><i className="ri-folder-user-line text-primary-600" /> 档案信息</p>
                <dl className="mt-3 space-y-2.5 text-sm">
                  <div className="flex justify-between"><dt className="text-foreground-400">所属分类</dt><dd className="font-medium text-foreground-900">{cat.name}黑料</dd></div>
                  <div className="flex justify-between"><dt className="text-foreground-400">细分标签</dt><dd className="font-medium text-foreground-900">{item.tags.length} 个</dd></div>
                  <div className="flex justify-between"><dt className="text-foreground-400">阅读时长</dt><dd className="font-medium text-foreground-900">{item.read}</dd></div>
                  <div className="flex justify-between"><dt className="text-foreground-400">事件热度</dt><dd className="inline-flex items-center gap-1 text-accent-700"><i className="ri-fire-fill" />{item.heat}/5</dd></div>
                </dl>
                <Link to="/about#submit" className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-primary-300 bg-primary-50 px-3 py-2 text-xs font-semibold text-primary-700 hover:bg-primary-100 transition-colors whitespace-nowrap">
                  <i className="ri-lightbulb-flash-line" /> 补充这条线索
                </Link>
              </div>
            </div>
          </aside>
        </div>

        {/* 相关推荐 */}
        {related.length > 0 && (
          <div className="mx-auto max-w-[1200px] px-4 md:px-6 pb-12 md:pb-16">
            <div className="flex items-end justify-between gap-4">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">同分类 · 相关档案</h2>
              <Link to={`/archive?cat=${cat.slug}`} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:text-primary-800 transition-colors whitespace-nowrap">
                更多{cat.name}档案 <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <article key={r.slug} className="group flex flex-col rounded-xl border border-background-300/70 bg-background-100 p-5 transition-all hover:-translate-y-1 hover:border-primary-300">
                  <span className="text-[11px] text-foreground-400">{r.published}</span>
                  <h3 className="mt-2 font-heading text-base font-bold leading-7 text-foreground-950 group-hover:text-primary-700 transition-colors line-clamp-2">
                    <Link to={`/case/${r.slug}`}>{r.title}</Link>
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600 line-clamp-2">{r.excerpt}</p>
                  <Link to={`/case/${r.slug}`} className="mt-auto pt-3 text-sm font-semibold text-primary-700 hover:text-primary-800 transition-colors">
                    阅读档案 →
                  </Link>
                </article>
              ))}
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
}